import { formatTextOrDash } from '@/lib/format/text';
import { normalizeUnit } from '@/lib/utils/unitConversion';
import { buildChainOfCustodyOptions, buildEscaBonusCapOptions } from './editDeclarationOptions';

// The Edit declaration form's values → what the ISCC PoS V4.0 document prints, box by box.

const text = (value) => formatTextOrDash(value);

// The form's Yes/No answers arrive as booleans or as the option strings, from two routes.
const isYes = (value) => value === true || /^(yes|true|1)$/i.test(String(value ?? ''));
const isNo = (value) => value === false || /^(no|false|0)$/i.test(String(value ?? ''));

// Neither box is ticked when the field is unanswered — an empty pair, never a guessed No.
const yesNo = (value) => ({ yes: isYes(value), no: isNo(value) });

const percent = (value) => (value == null || value === '' ? '-' : `${value}%`);

const optionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label ?? text(null);

// The SD number is two controls on the form and one value on the document.
const sdNumber = (values) => [values.idPrefix, values.idNumber].filter(Boolean).join('-');

const METRIC_TONS = 'MT';
const CUBIC_METERS = 'm³';

/** `values` is the Edit declaration form's; every string is display-ready. */
export const buildDeclarationDocument = (t, values = {}) => {
  const unit = normalizeUnit(values.unit);

  return {
    posNumber: text(sdNumber(values)),
    dateOfIssuance: text(values.dateOfIssuance),
    dateOfDispatch: text(values.dateOfDispatch),

    supplier: {
      name: text(values.supplierName),
      address: text(values.supplierAddress),
      certificateNumber: text(values.supplierCertificateNumber),
    },
    recipient: {
      name: text(values.recipientName),
      address: text(values.recipientAddress),
      contractNumber: text(values.contractNumber),
    },
    dispatchAddress: text(values.supplierDispatchAddress),
    dispatchSameAsSupplier: values.dispatchSameAsSupplier === true,
    receiptAddress: text(values.recipientReceiptAddress),
    receiptSameAsRecipient: values.receiptSameAsRecipient === true,

    typeOfProduct: text(values.typeOfProduct),
    rawMaterialType: text(values.rawMaterialType),
    additionalInformation: text(values.additionalInformation),
    countryOfOrigin: text(values.countryOfOrigin),
    quantity: text(values.totalCertifiedQuantity),
    unitCubicMeters: unit === CUBIC_METERS,
    unitMetricTons: unit === METRIC_TONS,
    energyContent: text(values.energyContent),
    euRedCompliant: isYes(values.euRedCompliantMaterial),
    isccCompliant: isYes(values.isccCompliantMaterial),
    chainOfCustody: optionLabel(buildChainOfCustodyOptions(t), values.chainOfCustodyOption),
    countryOfBiofuelProduction: text(values.countryOfBiofuelProduction),
    startDateOfBiofuelProduction: text(values.startDateOfBiofuelProduction),
    startDateOfBioliquidUse: text(values.startDateOfBioliquidUse),

    sustainabilityCriteria: yesNo(values.sustainabilityCriteriaCompliance),
    intermediateCrop: yesNo(values.intermediateCrop),
    lowIlucRisk: yesNo(values.lowIlucRisk),
    wasteOrResidue: yesNo(values.wasteOrResidue),
    wastePermitNumber: text(values.wastePermitNumber),
    supportReceived: yesNo(values.supportReceived),
    supportNatureAndScheme: text(values.supportNatureAndScheme),

    redIiiTotalValue: yesNo(values.defaultValueApplied),
    // Only the "No" branch collected the terms; read like the tick, as a stored declaration sends 'no'.
    showBreakdown: isNo(values.defaultValueApplied),
    totalGhgEmissions: text(values.totalGhgEmissions),
    breakdown: {
      eec: text(values.eec),
      el: text(values.el),
      ep: text(values.ep),
      etd: text(values.etd),
      eu: text(values.eu),
      esca: text(values.esca),
      eccs: text(values.eccs),
      eccr: text(values.eccr),
    },
    allocatedHeat: text(values.allocatedHeat),
    allocatedElectricity: text(values.allocatedElectricity),
    escaBonusCap: optionLabel(buildEscaBonusCapOptions(t), values.escaBonusCap),
    nuts2Area: text(values.nuts2Area),

    saving: {
      transport: percent(values.savingTransport),
      electricity: percent(values.savingElectricity),
      electricityOutermost: percent(values.savingElectricityOutermost),
      usefulHeat: percent(values.savingUsefulHeat),
      heatSubstitution: percent(values.savingHeatSubstitution),
    },

    addons: {
      foodSecurityStandard: isYes(values.foodSecurityStandard),
      euDeforestationRegulation: isYes(values.euDeforestationRegulation),
      nonGmoFoodFeed: isYes(values.nonGmoFoodFeed),
      nonGmoTechnicalMarkets: isYes(values.nonGmoTechnicalMarkets),
    },
  };
};

// Form fields the PoS V4.0 has no box for — they reach the server, never the document.
export const NOT_ON_DOCUMENT = Object.freeze([
  'idPrefix',
  'shippingDistanceKm',
  'trainDistanceKm',
  'roadDistanceKm',
  'disaggregatedDefaultValueForOil',
  'disaggregatedDefaultValueForSoil',
  'elBonusObtained',
]);

export const declarationFileName = (values = {}) =>
  `Declaration_${sdNumber(values) || 'draft'}.pdf`;
